import PropTypes from 'prop-types';
import React from 'react';
import Modal from '../../containers/windowed-modal.jsx';
import Box from '../box/box.jsx';
import ColorPicker from './color-picker.jsx';
import FancyCheckbox from '../tw-fancy-checkbox/checkbox.jsx';
import {defineMessages, injectIntl, intlShape, FormattedMessage} from 'react-intl';

import booleanInputIcon from './icon--boolean-input.svg';
import textInputIcon from './icon--text-input.svg';
import labelIcon from './icon--label.svg';

import styles from './custom-procedures.css';

const messages = defineMessages({
    myblockModalTitle: {
        defaultMessage: 'Make a Block',
        description: 'Title for the modal where you create a custom block.',
        id: 'gui.customProcedures.myblockModalTitle'
    }
});

/* Inline SVG icon for the "Add branch" button — a C-block shape */
const BranchIcon = () => (
    <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <rect x="2" y="4" width="20" height="8" rx="3" fill="currentColor" opacity="0.85"/>
        <rect x="2" y="4" width="4" height="22" rx="2" fill="currentColor" opacity="0.85"/>
        <rect x="2" y="20" width="20" height="6" rx="3" fill="currentColor" opacity="0.85"/>
        <rect x="6" y="12" width="14" height="8" rx="2" fill="currentColor" opacity="0.3"/>
    </svg>
);

const CustomProcedures = props => {
    const [colorExpanded, setColorExpanded] = React.useState(false);

    return (
        <Modal
            className={styles.modalContent}
            contentLabel={props.intl.formatMessage(messages.myblockModalTitle)}
            onRequestClose={props.onCancel}
            id="customProceduresModal"
            width={800}
            height={700}
            resizable={false}
            maximizable={false}
        >
            <Box
                className={styles.workspace}
                componentRef={props.componentRef}
            />
            <Box className={styles.body}>
                <Box className={styles.toolbarSection}>
                    <Box className={styles.toolbar}>
                        <button
                            className={styles.toolbarButton}
                            onClick={props.onAddTextNumber}
                            title={props.intl.formatMessage({
                                defaultMessage: 'Add number or text input',
                                id: 'gui.customProcedures.numberTextType'
                            })}
                        >
                            <img
                                src={textInputIcon}
                                draggable={false}
                            />
                            <span>
                                <FormattedMessage
                                    defaultMessage="Add number or text"
                                    description="Label for button to add a number/text input"
                                    id="gui.customProcedures.addAnInputNumberText"
                                />
                            </span>
                        </button>
                        <button
                            className={styles.toolbarButton}
                            onClick={props.onAddBoolean}
                            title={props.intl.formatMessage({
                                defaultMessage: 'Add boolean input',
                                id: 'gui.customProcedures.booleanType'
                            })}
                        >
                            <img
                                src={booleanInputIcon}
                                draggable={false}
                            />
                            <span>
                                <FormattedMessage
                                    defaultMessage="Add boolean"
                                    description="Label for button to add a boolean input"
                                    id="gui.customProcedures.addAnInputBoolean"
                                />
                            </span>
                        </button>
                        <button
                            className={styles.toolbarButton}
                            onClick={props.onAddLabel}
                            title={props.intl.formatMessage({
                                defaultMessage: 'Add a label',
                                id: 'gui.customProcedures.addALabel'
                            })}
                        >
                            <img
                                src={labelIcon}
                                draggable={false}
                            />
                            <span>
                                <FormattedMessage
                                    defaultMessage="Add label"
                                    description="Label for button to add a label"
                                    id="gui.customProcedures.addALabel"
                                />
                            </span>
                        </button>
                        <button
                            className={styles.toolbarButton}
                            onClick={props.onAddBranch}
                            title={props.intl.formatMessage({
                                defaultMessage: 'Add a branch (C-slot)',
                                id: 'gui.customProcedures.addABranch'
                            })}
                        >
                            <BranchIcon />
                            <span>
                                <FormattedMessage
                                    defaultMessage="Add branch"
                                    description="Label for button to add a branch C-slot to the block"
                                    id="gui.customProcedures.addABranch"
                                />
                            </span>
                        </button>
                    </Box>
                </Box>

                {/* Collapsible color section */}
                <Box className={styles.collapsibleSection}>
                    <button
                        className={styles.collapsibleHeader}
                        onClick={() => setColorExpanded(e => !e)}
                        type="button"
                    >
                        <span className={styles.collapsibleTitle}>
                            <FormattedMessage
                                defaultMessage="Block color"
                                description="Header for collapsible block color section"
                                id="gui.customProcedures.blockColor"
                            />
                        </span>
                        <span
                            className={styles.colorSwatch}
                            style={{backgroundColor: props.color || '#FF6680'}}
                        />
                        <span className={`${styles.collapseArrow} ${colorExpanded ? styles.collapseArrowOpen : ''}`}>
                            {'▶'}
                        </span>
                    </button>
                    {colorExpanded && (
                        <Box className={styles.collapsibleBody}>
                            <ColorPicker
                                color={props.color || '#FF6680'}
                                onColorChange={props.onColorChange}
                            />
                        </Box>
                    )}
                </Box>

                {/* Warp */}
                <Box className={styles.warpSection}>
                    <label className={styles.checkboxRow}>
                        <FancyCheckbox
                            checked={props.warp}
                            onChange={props.onToggleWarp}
                        />
                        <FormattedMessage
                            defaultMessage="Run without screen refresh"
                            description="Label for checkbox to run without screen refresh"
                            id="gui.customProcedures.runWithoutScreenRefresh"
                        />
                    </label>
                </Box>

                <Box className={styles.buttonRow}>
                    <button
                        className={styles.cancelButton}
                        onClick={props.onCancel}
                    >
                        <FormattedMessage
                            defaultMessage="Cancel"
                            description="Label for button to cancel custom procedure edits"
                            id="gui.customProcedures.cancel"
                        />
                    </button>
                    <button
                        className={styles.okButton}
                        onClick={props.onOk}
                    >
                        <FormattedMessage
                            defaultMessage="OK"
                            description="Label for button to save new custom procedure"
                            id="gui.customProcedures.ok"
                        />
                    </button>
                </Box>
            </Box>
        </Modal>
    );
};

CustomProcedures.propTypes = {
    color: PropTypes.string,
    componentRef: PropTypes.func.isRequired,
    intl: intlShape,
    onAddBoolean: PropTypes.func.isRequired,
    onAddBranch: PropTypes.func.isRequired,
    onAddLabel: PropTypes.func.isRequired,
    onAddTextNumber: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onColorChange: PropTypes.func.isRequired,
    onOk: PropTypes.func.isRequired,
    onToggleWarp: PropTypes.func.isRequired,
    warp: PropTypes.bool.isRequired
};

export default injectIntl(CustomProcedures);
